import { Connection, PublicKey } from '@solana/web3.js';

/**
 * Transaction Parser for Prediction Market Positions
 * 
 * This parser is specifically for parsing prediction market smart contract events:
 * - PositionOpened events (to get position entry data)
 * - PositionClosed events (to get exit data and profit)
 * 
 * This is different from transactionParsingService.js which parses token swap transactions.
 * 
 * Key differences:
 * - Uses Anchor event discriminators from the IDL
 * - Parses structured event data, not SOL/token movements
 * - Extracts cycle_id, position data, and profit information
 */

export interface ParsedPositionEntry {
  direction: 'long' | 'short';
  entryPrice: number;
  betAmount: number;
  cycleId: string;
  userPubkey: string;
  assetAddress: string;
}

export interface ParsedPositionExit {
  exitPrice: number;
  profit: number;
  cycleId: string;
  userPubkey: string;
  becameKing: boolean;
  assetAddress: string;
}

export interface ParsedCrownClaimed {
  cycleId: string;
  winner: string;
  winnerAmount: number;
  platformFee: number;
  endCycleAsset?: string; // Optional asset address from the cycle
}

/**
 * Parse position entry transaction to extract position data from PositionOpened event
 */
export const parsePositionEntryTransaction = async (
  connection: Connection,
  signature: string
): Promise<ParsedPositionEntry> => {
  try {
    const tx = await connection.getTransaction(signature, {
      commitment: 'confirmed',
      maxSupportedTransactionVersion: 0
    });

    if (!tx || !tx.meta) {
      throw new Error('Transaction not found or no metadata');
    }

    if (tx.meta.err) {
      throw new Error(`Transaction failed: ${JSON.stringify(tx.meta.err)}`);
    }

    // Parse program logs for position entry event
    const logs = tx.meta.logMessages || [];
    const positionEvent = parseProgramLogsForPositionEntry(logs);
    
    if (!positionEvent) {
      throw new Error('Position entry event not found in transaction logs');
    }

    return {
      direction: positionEvent.direction,
      entryPrice: positionEvent.entryPrice,
      betAmount: positionEvent.betAmount,
      cycleId: positionEvent.cycleId,
      userPubkey: positionEvent.userPubkey,
      assetAddress: positionEvent.assetAddress
    };

  } catch (error) {
    console.error('Failed to parse position entry transaction:', error);
    throw error;
  }
};

/**
 * Parse crown claimed transaction to extract cycle completion data
 */
export const parseCrownClaimedTransaction = async (
  connection: Connection,
  signature: string
): Promise<ParsedCrownClaimed> => {
  try {
    const tx = await connection.getTransaction(signature, {
      commitment: 'confirmed',
      maxSupportedTransactionVersion: 0
    });

    if (!tx || !tx.meta) {
      throw new Error('Transaction not found or no metadata');
    }

    if (tx.meta.err) {
      throw new Error(`Transaction failed: ${JSON.stringify(tx.meta.err)}`);
    }

    // Parse program logs for crown claimed event
    const logs = tx.meta.logMessages || [];
    const crownEvent = parseProgramLogsForCrownClaimed(logs);
    
    if (!crownEvent) {
      throw new Error('CrownClaimed event not found in transaction logs');
    }

    return {
      cycleId: crownEvent.cycleId,
      winner: crownEvent.winner,
      winnerAmount: crownEvent.winnerAmount,
      platformFee: crownEvent.platformFee
    };

  } catch (error) {
    console.error('Failed to parse crown claimed transaction:', error);
    throw error;
  }
};

/**
 * Parse position exit transaction to extract exit data
 */
export const parsePositionExitTransaction = async (
  connection: Connection,
  signature: string
): Promise<ParsedPositionExit> => {
  try {
    const tx = await connection.getTransaction(signature, {
      commitment: 'confirmed',
      maxSupportedTransactionVersion: 0
    });

    if (!tx || !tx.meta) {
      throw new Error('Transaction not found or no metadata');
    }

    if (tx.meta.err) {
      throw new Error(`Transaction failed: ${JSON.stringify(tx.meta.err)}`);
    }

    // Parse program logs for position exit event
    const logs = tx.meta.logMessages || [];
    const exitEvent = parseProgramLogsForPositionExit(logs);
    
    if (!exitEvent) {
      throw new Error('Position exit event not found in transaction logs');
    }

    return {
      exitPrice: exitEvent.exitPrice,
      profit: exitEvent.profit,
      cycleId: exitEvent.cycleId,
      userPubkey: exitEvent.userPubkey,
      becameKing: exitEvent.becameKing,
      assetAddress: exitEvent.assetAddress
    };

  } catch (error) {
    console.error('Failed to parse position exit transaction:', error);
    throw error;
  }
};

/**
 * Parse program logs for position entry event
 * Based on the actual IDL structure from prediction_market.json
 */
const parseProgramLogsForPositionEntry = (logs: string[]) => {
  // PositionOpened event discriminator from IDL: [237, 175, 243, 230, 147, 117, 101, 121]
  const POSITION_OPENED_DISCRIMINATOR = [237, 175, 243, 230, 147, 117, 101, 121];
  
  for (const log of logs) {
    // Method 1: Look for Anchor event logs with base64 encoded data
    if (log.includes('Program data:')) {
      try {
        // Extract base64 data after "Program data: "
        const base64Data = log.split('Program data: ')[1];
        if (base64Data) {
          const eventData = Buffer.from(base64Data, 'base64');
          
          // Check if this matches PositionOpened discriminator
          if (eventData.length >= 8) {
            const discriminator = Array.from(eventData.subarray(0, 8));
            if (JSON.stringify(discriminator) === JSON.stringify(POSITION_OPENED_DISCRIMINATOR)) {
              
              // Parse the event data according to IDL structure:
              // PositionOpened { user: pubkey, cycle_id: u64, bet_amount: u64, entry_price_usd: i64, direction: Direction }
              
              let offset = 8; // Skip discriminator
              
              // user: pubkey (32 bytes)
              const userPubkey = eventData.subarray(offset, offset + 32);
              offset += 32;
              
              // cycle_id: u64 (8 bytes, little endian)
              const cycleId = eventData.readBigUInt64LE(offset);
              offset += 8;
              
              // bet_amount: u64 (8 bytes, little endian)
              const betAmount = eventData.readBigUInt64LE(offset);
              offset += 8;
              
              // entry_price_usd: i64 (8 bytes, little endian)
              const entryPrice = eventData.readBigInt64LE(offset);
              offset += 8;
              
              // direction: Direction enum (1 byte: 0 = Long, 1 = Short)
              const directionByte = eventData.readUInt8(offset);
              const direction = directionByte === 0 ? 'long' : 'short';
              offset += 1;
              
              // asset_address: string (4 bytes length prefix + string bytes)
              const assetAddressLength = eventData.readUInt32LE(offset);
              offset += 4;
              const assetAddress = eventData.subarray(offset, offset + assetAddressLength).toString('utf8');
              offset += assetAddressLength;
              
              console.log('🔍 Transaction parser debug:', {
                rawDirectionByte: directionByte,
                interpretedDirection: direction,
                assetAddress: assetAddress,
                expectedForLong: 'should be 0 for long'
              });
              
              return {
                direction: direction as 'long' | 'short',
                entryPrice: Number(entryPrice),
                betAmount: Number(betAmount),
                cycleId: cycleId.toString(),
                userPubkey: new PublicKey(userPubkey).toString(), // Convert to base58
                assetAddress: assetAddress
              };
            }
          }
        }
      } catch (error) {
        console.warn('Failed to parse event data:', error);
        continue;
      }
    }
    
    // Method 2: Fallback to string parsing for human-readable logs
    if (log.includes('PositionOpened')) {
      // Look for patterns like: "PositionOpened { user: ABC..., cycle_id: 123, bet_amount: 15000000, entry_price_usd: 1500000000, direction: Long, asset_address: So11111... }"
      const match = log.match(/PositionOpened\s*\{([^}]+)\}/);
      if (match) {
        const eventData = match[1];
        
        // Extract individual fields
        const directionMatch = eventData.match(/direction:\s*(\w+)/);
        const entryPriceMatch = eventData.match(/entry_price_usd:\s*(-?\d+)/);
        const betAmountMatch = eventData.match(/bet_amount:\s*(\d+)/);
        const cycleIdMatch = eventData.match(/cycle_id:\s*(\d+)/);
        const userMatch = eventData.match(/user:\s*([A-Za-z0-9]{32,})/);
        const assetAddressMatch = eventData.match(/asset_address:\s*"?([A-Za-z0-9]{32,})"?/);
        
        if (directionMatch && entryPriceMatch && betAmountMatch && cycleIdMatch && userMatch && assetAddressMatch) {
          return {
            direction: directionMatch[1].toLowerCase() as 'long' | 'short',
            entryPrice: parseInt(entryPriceMatch[1]),
            betAmount: parseInt(betAmountMatch[1]),
            cycleId: cycleIdMatch[1],
            userPubkey: userMatch[1],
            assetAddress: assetAddressMatch[1]
          };
        }
      }
    }
  }
  
  return null;
};

/**
 * Parse program logs for position exit event
 * Based on the actual IDL structure from prediction_market.json
 */
const parseProgramLogsForPositionExit = (logs: string[]) => {
  // PositionClosed event discriminator from IDL: [157, 163, 227, 228, 13, 97, 138, 121]
  const POSITION_CLOSED_DISCRIMINATOR = [157, 163, 227, 228, 13, 97, 138, 121];
  
  for (const log of logs) {
    // Method 1: Look for Anchor event logs with base64 encoded data
    if (log.includes('Program data:')) {
      try {
        // Extract base64 data after "Program data: "
        const base64Data = log.split('Program data: ')[1];
        if (base64Data) {
          const eventData = Buffer.from(base64Data, 'base64');
          
          // Check if this matches PositionClosed discriminator
          if (eventData.length >= 8) {
            const discriminator = Array.from(eventData.subarray(0, 8));
            if (JSON.stringify(discriminator) === JSON.stringify(POSITION_CLOSED_DISCRIMINATOR)) {
              
              // Parse the event data according to IDL structure:
              // PositionClosed { user: pubkey, cycle_id: u64, exit_price_usd: i64, profit: i64, became_king: bool }
              
              let offset = 8; // Skip discriminator
              
              // user: pubkey (32 bytes)
              const userPubkey = eventData.subarray(offset, offset + 32);
              offset += 32;
              
              // cycle_id: u64 (8 bytes, little endian)
              const cycleId = eventData.readBigUInt64LE(offset);
              offset += 8;
              
              // exit_price_usd: i64 (8 bytes, little endian)
              const exitPrice = eventData.readBigInt64LE(offset);
              offset += 8;
              
              // profit: i64 (8 bytes, little endian)
              const profit = eventData.readBigInt64LE(offset);
              offset += 8;
              
              // became_king: bool (1 byte)
              const becameKing = eventData.readUInt8(offset) === 1;
              offset += 1;
              
              // asset_address: string (4 bytes length prefix + string bytes)
              const assetAddressLength = eventData.readUInt32LE(offset);
              offset += 4;
              const assetAddress = eventData.subarray(offset, offset + assetAddressLength).toString('utf8');
              offset += assetAddressLength;
              
              return {
                exitPrice: Number(exitPrice),
                profit: Number(profit),
                cycleId: cycleId.toString(),
                userPubkey: new PublicKey(userPubkey).toString(), // Convert to base58
                becameKing,
                assetAddress
              };
            }
          }
        }
      } catch (error) {
        console.warn('Failed to parse exit event data:', error);
        continue;
      }
    }
    
    // Method 2: Fallback to string parsing for human-readable logs
    if (log.includes('PositionClosed')) {
      // Look for patterns like: "PositionClosed { user: ABC..., cycle_id: 123, exit_price_usd: 1600000000, profit: 100000000, became_king: true, asset_address: So11111... }"
      const match = log.match(/PositionClosed\s*\{([^}]+)\}/);
      if (match) {
        const eventData = match[1];
        
        const exitPriceMatch = eventData.match(/exit_price_usd:\s*(-?\d+)/);
        const profitMatch = eventData.match(/profit:\s*(-?\d+)/);
        const cycleIdMatch = eventData.match(/cycle_id:\s*(\d+)/);
        const userMatch = eventData.match(/user:\s*([A-Za-z0-9]{32,})/);
        const becameKingMatch = eventData.match(/became_king:\s*(true|false)/);
        const assetAddressMatch = eventData.match(/asset_address:\s*"?([A-Za-z0-9]{32,})"?/);
        
        if (exitPriceMatch && profitMatch && cycleIdMatch && userMatch && becameKingMatch && assetAddressMatch) {
          return {
            exitPrice: parseInt(exitPriceMatch[1]),
            profit: parseInt(profitMatch[1]),
            cycleId: cycleIdMatch[1],
            userPubkey: userMatch[1],
            becameKing: becameKingMatch[1] === 'true',
            assetAddress: assetAddressMatch[1]
          };
        }
      }
    }
  }
  
  return null;
};

/**
 * Parse program logs for CrownClaimed event
 * Based on the CrownClaimed event structure: { cycle_id: u64, winner: Pubkey, winner_amount: u64, platform_fee: u64 }
 */
const parseProgramLogsForCrownClaimed = (logs: string[]) => {
  // CrownClaimed event discriminator from IDL: [178, 71, 234, 19, 85, 28, 224, 158]
  const CROWN_CLAIMED_DISCRIMINATOR = [178, 71, 234, 19, 85, 28, 224, 158];
  
  for (const log of logs) {
    // Method 1: Look for Anchor event logs with base64 encoded data
    if (log.includes('Program data:')) {
      try {
        // Extract base64 data after "Program data: "
        const base64Data = log.split('Program data: ')[1];
        if (base64Data) {
          const eventData = Buffer.from(base64Data, 'base64');
          
          // Check if this matches CrownClaimed discriminator
          if (eventData.length >= 8) {
            const discriminator = Array.from(eventData.subarray(0, 8));
            if (JSON.stringify(discriminator) === JSON.stringify(CROWN_CLAIMED_DISCRIMINATOR)) {
              
              // Parse the event data according to CrownClaimed structure:
              // CrownClaimed { cycle_id: u64, winner: Pubkey, winner_amount: u64, platform_fee: u64, end_cycle_asset: Option<String> }
              
              let offset = 8; // Skip discriminator
              
              // cycle_id: u64 (8 bytes, little endian)
              const cycleId = eventData.readBigUInt64LE(offset);
              offset += 8;
              
              // winner: pubkey (32 bytes)
              const winnerPubkey = eventData.subarray(offset, offset + 32);
              offset += 32;
              
              // winner_amount: u64 (8 bytes, little endian)
              const winnerAmount = eventData.readBigUInt64LE(offset);
              offset += 8;
              
              // platform_fee: u64 (8 bytes, little endian)
              const platformFee = eventData.readBigUInt64LE(offset);
              offset += 8;
              
              // end_cycle_asset: Option<String> (1 byte for Some/None + string if Some)
              let endCycleAsset: string | undefined;
              console.log(`🔍 CrownClaimed asset parsing debug:`, {
                eventDataLength: eventData.length,
                currentOffset: offset,
                remainingBytes: eventData.length - offset
              });
              
              if (offset < eventData.length) {
                const hasAssetByte = eventData.readUInt8(offset);
                const hasAsset = hasAssetByte === 1; // 1 = Some, 0 = None
                offset += 1;
                
                console.log(`🔍 Asset option byte: ${hasAssetByte} (${hasAsset ? 'Some' : 'None'})`);
                
                if (hasAsset && offset + 4 <= eventData.length) {
                  const assetLength = eventData.readUInt32LE(offset);
                  offset += 4;
                  
                  console.log(`🔍 Asset length: ${assetLength}`);
                  
                  if (offset + assetLength <= eventData.length) {
                    endCycleAsset = eventData.subarray(offset, offset + assetLength).toString('utf8');
                    console.log(`🔍 Parsed asset address: ${endCycleAsset}`);
                  } else {
                    console.warn(`⚠️ Asset length exceeds remaining data: ${assetLength} > ${eventData.length - offset}`);
                  }
                } else if (hasAsset) {
                  console.warn(`⚠️ Asset marked as Some but insufficient data remaining`);
                }
              } else {
                console.log(`🔍 No remaining bytes for asset field - event may not include asset`);
              }
              
              return {
                cycleId: cycleId.toString(),
                winner: new PublicKey(winnerPubkey).toString(), // Convert to base58
                winnerAmount: Number(winnerAmount),
                platformFee: Number(platformFee),
                endCycleAsset
              };
            }
          }
        }
      } catch (error) {
        console.warn('Failed to parse crown claimed event data:', error);
        continue;
      }
    }
    
    // Method 2: Fallback to string parsing for human-readable logs
    if (log.includes('CrownClaimed')) {
      console.log(`🔍 CrownClaimed string parsing - raw log:`, log);
      
      // Look for patterns like: "CrownClaimed { cycle_id: 123, winner: ABC..., winner_amount: 1000000000, platform_fee: 50000000, end_cycle_asset: Some("So11111...") }"
      const match = log.match(/CrownClaimed\s*\{([^}]+)\}/);
      if (match) {
        const eventData = match[1];
        console.log(`🔍 Extracted event data:`, eventData);
        
        const cycleIdMatch = eventData.match(/cycle_id:\s*(\d+)/);
        const winnerMatch = eventData.match(/winner:\s*([A-Za-z0-9]{32,})/);
        const winnerAmountMatch = eventData.match(/winner_amount:\s*(\d+)/);
        const platformFeeMatch = eventData.match(/platform_fee:\s*(\d+)/);
        const endCycleAssetMatch = eventData.match(/end_cycle_asset:\s*(?:Some\("?([A-Za-z0-9]{32,})"?\)|None)/);
        
        console.log(`🔍 Regex matches:`, {
          cycleId: cycleIdMatch?.[1],
          winner: winnerMatch?.[1],
          winnerAmount: winnerAmountMatch?.[1], 
          platformFee: platformFeeMatch?.[1],
          endCycleAsset: endCycleAssetMatch?.[1]
        });
        
        if (cycleIdMatch && winnerMatch && winnerAmountMatch && platformFeeMatch) {
          const result = {
            cycleId: cycleIdMatch[1],
            winner: winnerMatch[1],
            winnerAmount: parseInt(winnerAmountMatch[1]),
            platformFee: parseInt(platformFeeMatch[1]),
            endCycleAsset: endCycleAssetMatch && endCycleAssetMatch[1] ? endCycleAssetMatch[1] : undefined
          };
          console.log(`🔍 CrownClaimed string parsing result:`, result);
          return result;
        }
      }
    }
  }
  
  return null;
};

/**
 * Parse a transaction that may contain both CrownClaimed and PositionOpened events
 * (for bundled start_new_cycle + open_position transactions)
 */
export const parseBundledCycleTransaction = async (
  connection: Connection,
  signature: string
): Promise<{ crownClaimed: ParsedCrownClaimed | null; positionOpened: ParsedPositionEntry | null }> => {
  try {
    const tx = await connection.getTransaction(signature, {
      commitment: 'confirmed',
      maxSupportedTransactionVersion: 0
    });

    if (!tx || !tx.meta) {
      throw new Error('Transaction not found or no metadata');
    }

    if (tx.meta.err) {
      throw new Error(`Transaction failed: ${JSON.stringify(tx.meta.err)}`);
    }

    const logs = tx.meta.logMessages || [];
    
    // Parse both events from the same transaction
    const crownEvent = parseProgramLogsForCrownClaimed(logs);
    const positionEvent = parseProgramLogsForPositionEntry(logs);
    
    return {
      crownClaimed: crownEvent ? {
        cycleId: crownEvent.cycleId,
        winner: crownEvent.winner,
        winnerAmount: crownEvent.winnerAmount,
        platformFee: crownEvent.platformFee,
        endCycleAsset: crownEvent.endCycleAsset
      } : null,
      positionOpened: positionEvent ? {
        direction: positionEvent.direction,
        entryPrice: positionEvent.entryPrice,
        betAmount: positionEvent.betAmount,
        cycleId: positionEvent.cycleId,
        userPubkey: positionEvent.userPubkey,
        assetAddress: positionEvent.assetAddress
      } : null
    };

  } catch (error) {
    console.error('Failed to parse bundled cycle transaction:', error);
    throw error;
  }
};

/**
 * Helper function to wait for transaction confirmation with retry logic
 * Returns true ONLY if transaction is confirmed AND succeeded (no errors)
 */
export const waitForTransactionConfirmation = async (
  connection: Connection,
  signature: string,
  maxRetries: number = 30,
  retryInterval: number = 1000
): Promise<boolean> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const status = await connection.getSignatureStatus(signature);
      
      // ✅ Check if transaction is confirmed or finalized
      if (status.value?.confirmationStatus === 'confirmed' || 
          status.value?.confirmationStatus === 'finalized') {
        
        // ⚠️ CRITICAL: Check if transaction actually succeeded (no errors)
        if (status.value?.err) {
          console.error('❌ Transaction confirmed but FAILED on-chain:', status.value.err);
          return false;
        }
        
        // ✅ Transaction confirmed AND succeeded
        console.log('✅ Transaction confirmed and succeeded');
        return true;
      }
      
      // ❌ Check for early failure detection
      if (status.value?.err) {
        console.error('❌ Transaction failed before confirmation:', status.value.err);
        return false;
      }
      
    } catch (error) {
      console.warn(`Confirmation check ${i + 1} failed:`, error);
    }
    
    // Wait before next check
    await new Promise(resolve => setTimeout(resolve, retryInterval));
  }
  
  console.warn(`❌ Transaction confirmation timeout after ${maxRetries} retries`);
  return false;
};